export const NODE_TYPE_MESSAGE = 'Message'
export const NODE_TYPE_CONTACT = 'UpdateContact'
export const NODE_TYPE_TEMPLATE = 'SendTemplate'
export const NODE_TYPE_START_CHATBOT = 'StartChatbot'
export const NODE_TYPE_AGENT = 'TriggerAgent'
export const EDGE_TYPE_DELETABLE = 'deletable'
export const HANDLE_OUT = 'out'
export const HANDLE_DEFAULT = 'default'
export const HANDLE_AGENT_DONE = 'done'
export const HANDLE_AGENT_ERROR = 'error'
export const HANDLE_AGENT_TIMEOUT = 'timeout'

export const AGENT_HANDLES = [
  { id: HANDLE_AGENT_DONE, label: 'Conversation ended', icon: 'check_circle' },
  { id: HANDLE_AGENT_ERROR, label: 'Error', icon: 'error_outline' },
  { id: HANDLE_AGENT_TIMEOUT, label: 'Timed out', icon: 'schedule' },
]

export const HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']
export const INTERACTIVITY_TYPES = ['NONE', 'BUTTON', 'LIST']

export const NODE_PALETTE = [
  { type: NODE_TYPE_MESSAGE, label: 'Send Message', start: true, description: 'Send a WhatsApp message to start the conversation.' },
  { type: NODE_TYPE_TEMPLATE, label: 'Send Template', start: true, description: 'Send an approved WhatsApp template to start the conversation.' },
  { type: NODE_TYPE_CONTACT, label: 'Update Contact' },
  { type: NODE_TYPE_START_CHATBOT, label: 'Trigger Chatbot' },
]

/** Only offered when the tenant has WHATSAPP_CHATBOT_AGENT enabled. */
export const AGENT_PALETTE_ITEM = {
  type: NODE_TYPE_AGENT,
  label: 'Trigger Agent',
  start: true,
  description: 'Hand the conversation to an AI agent until it ends.',
}

export function getNodePalette(options) {
  const opts = options || {}
  return opts.agentEnabled ? NODE_PALETTE.concat(AGENT_PALETTE_ITEM) : NODE_PALETTE
}

export const CHATBOT_JSON_VERSION = '1.0.0'

function emptyNormalized() {
  return {
    headerType: 'NONE',
    headerText: '',
    media: '',
    mediaLink: '',
    fileName: '',
    bodyText: '',
    bodyRichText: '',
    footerText: '',
    interactivityType: 'NONE',
    buttons: [],
    listLabel: '',
    listSectionTitle: '',
    listRows: [],
  }
}

export function normalizeNodeData(data) {
  const d = data || {}
  if (d.headerType || d.interactivityType) {
    return {
      headerType: d.headerType || 'NONE',
      headerText: d.headerText || '',
      media: d.media || '',
      mediaLink: d.mediaLink || '',
      fileName: d.fileName || '',
      bodyText: d.bodyText || '',
      bodyRichText: d.bodyRichText || '',
      footerText: d.footerText || '',
      interactivityType: d.interactivityType || 'NONE',
      buttons: Array.isArray(d.buttons) ? d.buttons : [],
      listLabel: d.listLabel || '',
      listSectionTitle: d.listSectionTitle || '',
      listRows: Array.isArray(d.listRows) ? d.listRows : [],
    }
  }
  const mt = d.messageType || ''
  if (mt === 'IMAGE') {
    return Object.assign(emptyNormalized(), {
      headerType: 'IMAGE',
      media: d.image || '',
      mediaLink: d.imageLink || '',
      bodyText: d.caption || '',
    })
  }
  if (mt === 'VIDEO') {
    return Object.assign(emptyNormalized(), {
      headerType: 'VIDEO',
      media: d.video || '',
      mediaLink: d.videoLink || '',
      bodyText: d.caption || '',
    })
  }
  if (mt === 'DOCUMENT') {
    return Object.assign(emptyNormalized(), {
      headerType: 'DOCUMENT',
      media: d.attachment || '',
      mediaLink: d.attachmentsLink || '',
      fileName: d.fileName || '',
      bodyText: d.caption || '',
    })
  }
  if (mt === 'TEXT' || d.text) {
    return Object.assign(emptyNormalized(), {
      bodyText: d.text || '',
    })
  }
  return emptyNormalized()
}

export function getOutgoingHandles(data, nodeType) {
  if (nodeType === NODE_TYPE_START_CHATBOT) {
    return []
  }
  if (nodeType === NODE_TYPE_AGENT) {
    return AGENT_HANDLES.map(h => ({ id: h.id, label: h.label }))
  }
  if (nodeType === NODE_TYPE_TEMPLATE) {
    return getTemplateOutgoingHandles(data)
  }
  if (nodeType && nodeType !== NODE_TYPE_MESSAGE) {
    return [{ id: HANDLE_OUT, label: '' }]
  }
  const d = normalizeNodeData(data)
  const iType = d.interactivityType || 'NONE'
  if (iType === 'BUTTON') {
    const branch = (d.buttons || []).filter(b => {
      if (!b || !b.id) return false
      const t = b.type || 'QUICK_REPLY'
      return t === 'QUICK_REPLY' || t === 'FLOW'
    })
    if (branch.length) {
      return withDefaultHandle(branch.map(b => ({
        id: b.id,
        label: b.text || '',
        buttonType: b.type || 'QUICK_REPLY',
      })), data)
    }
    return [{ id: HANDLE_OUT, label: '' }]
  }
  if (iType === 'LIST') {
    const rows = (d.listRows || []).filter(r => r && r.id).map(r => ({
      id: listRowHandleId(r.id),
      label: r.title || r.id,
      description: r.description || '',
    }))
    if (rows.length) return withDefaultHandle(rows, data)
    return rows
  }
  return [{ id: HANDLE_OUT, label: '' }]
}

export function getOutgoingHandleIds(data, nodeType) {
  return getOutgoingHandles(data, nodeType).map(h => h.id)
}

function hasTemplateStructure(data) {
  if (data && data.waTemplate && Array.isArray(data.waTemplate.components) && data.waTemplate.components.length) {
    return true
  }
  if (data && Array.isArray(data.structure) && data.structure.length) return true
  return false
}

function isTemplateHandleWithoutStructure(handle, data) {
  if (handle === HANDLE_DEFAULT) return !!(data && data.defaultEnabled)
  return handle === HANDLE_OUT || /^tbtn\d+$/.test(String(handle || ''))
}

function isAllowedOutgoingHandle(sourceNode, handle) {
  const validHandles = getOutgoingHandleIds(sourceNode && sourceNode.data, sourceNode && sourceNode.type)
  if (validHandles.indexOf(handle) !== -1) return true
  if (sourceNode && sourceNode.type === NODE_TYPE_TEMPLATE && !hasTemplateStructure(sourceNode.data)) {
    return isTemplateHandleWithoutStructure(handle, sourceNode.data)
  }
  return false
}

export function listRowHandleId(rowId) {
  return 'row:' + String(rowId || '')
}

export function templateButtonHandleId(index) {
  return 'tbtn' + String(index)
}

function getTemplateOutgoingHandles(data) {
  const structure = (data && data.waTemplate && Array.isArray(data.waTemplate.components))
    ? data.waTemplate.components
    : ((data && Array.isArray(data.structure)) ? data.structure : [])
  const handles = []
  let index = 0
  for (let c = 0; c < structure.length; c++) {
    const comp = structure[c]
    if (!comp || String(comp.type || '').toUpperCase() !== 'BUTTONS') continue
    const buttons = Array.isArray(comp.buttons) ? comp.buttons : []
    for (let i = 0; i < buttons.length; i++) {
      const b = buttons[i]
      const t = String((b && b.type) || '').toUpperCase()
      const handleIndex = index
      index += 1
      if (t !== 'QUICK_REPLY' && t !== 'FLOW') continue
      handles.push({
        id: templateButtonHandleId(handleIndex),
        label: (b && b.text) || '',
        buttonType: t,
      })
    }
  }
  if (handles.length) return withDefaultHandle(handles, data)
  return [{ id: HANDLE_OUT, label: '' }]
}

function withDefaultHandle(handles, data) {
  const list = Array.isArray(handles) ? handles.slice() : []
  if (!list.length || (list.length === 1 && list[0].id === HANDLE_OUT)) return list
  if (!(data && data.defaultEnabled)) return list
  if (list.some(h => h.id === HANDLE_DEFAULT)) return list
  return list.concat([{ id: HANDLE_DEFAULT, label: 'Default', kind: 'default' }])
}

export function edgeSourceHandle(edge) {
  return (edge && edge.sourceHandle) || HANDLE_OUT
}

function nodePrefix(type) {
  if (type === NODE_TYPE_CONTACT) return 'upd'
  if (type === NODE_TYPE_TEMPLATE) return 'tpl'
  if (type === NODE_TYPE_START_CHATBOT) return 'scb'
  if (type === NODE_TYPE_AGENT) return 'agt'
  return 'msg'
}

export function persistTemplateNodeData(data) {
  const d = data || {}
  const comps = (d.waComponents && typeof d.waComponents === 'object' && !Array.isArray(d.waComponents))
    ? d.waComponents
    : {}
  const out = {
    waTemplateKey: (d.waTemplateKey || '').trim(),
    waComponents: comps,
  }
  if (d.defaultEnabled) out.defaultEnabled = true
  return out
}

function persistChatbotNodes(nodes) {
  return (nodes || []).map(n => {
    if (!n || n.type !== NODE_TYPE_TEMPLATE) return n
    return { ...n, data: persistTemplateNodeData(n.data) }
  })
}

function persistChatbotEdges(edges) {
  return (Array.isArray(edges) ? edges : []).map(e => ({
    id: e.id,
    sourceNodeId: e.sourceNodeId,
    sourceHandle: edgeSourceHandle(e),
    targetNodeId: e.targetNodeId,
  }))
}

export function defaultNodeData(type) {
  if (type === NODE_TYPE_MESSAGE) {
    return {
      headerType: 'NONE',
      bodyText: '',
      interactivityType: 'NONE',
    }
  }
  if (type === NODE_TYPE_CONTACT) {
    return {}
  }
  if (type === NODE_TYPE_TEMPLATE) {
    return {
      waTemplateKey: '',
      waComponents: {},
    }
  }
  if (type === NODE_TYPE_START_CHATBOT) {
    return { chatbotId: '' }
  }
  if (type === NODE_TYPE_AGENT) {
    return { providerId: '', openingMessage: '' }
  }
  return {}
}

export function createEmptyChatbot(options) {
  const opts = options || {}
  return {
    id: opts.id || '',
    name: opts.name || '',
    createdTs: opts.createdTs || '',
    state: opts.state || 'DISABLED',
    version: CHATBOT_JSON_VERSION,
    nodes: [],
    edges: [],
  }
}

export function botJsonFromChatbot(chatbot) {
  const c = chatbot || {}
  return JSON.stringify({
    version: c.version || CHATBOT_JSON_VERSION,
    nodes: persistChatbotNodes(c.nodes),
    edges: persistChatbotEdges(c.edges),
  })
}

function parseBotJson(value) {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch (e) {
      return {}
    }
  }
  if (typeof value === 'object') return value
  return {}
}

export function chatbotFromRecord(record) {
  const rec = record || {}
  const botJson = parseBotJson(rec.botJson)
  return enableConnectedDefaults(remapRetiredOutEdges({
    id: rec.id || '',
    name: rec.title || rec.name || '',
    createdTs: rec.createdTs || '',
    state: rec.state || 'DISABLED',
    version: botJson.version || CHATBOT_JSON_VERSION,
    nodes: persistChatbotNodes(Array.isArray(botJson.nodes) ? botJson.nodes : []),
    edges: persistChatbotEdges(botJson.edges),
  }))
}

function enableConnectedDefaults(chatbot) {
  const connected = {}
  ;((chatbot && chatbot.edges) || []).forEach(e => {
    if (edgeSourceHandle(e) === HANDLE_DEFAULT) connected[e.sourceNodeId] = true
  })
  const nodes = (chatbot && chatbot.nodes) || []
  if (!nodes.some(n => connected[n.id] && !(n.data && n.data.defaultEnabled))) return chatbot
  return {
    ...chatbot,
    nodes: nodes.map(n => (
      connected[n.id] && !(n.data && n.data.defaultEnabled)
        ? { ...n, data: { ...(n.data || {}), defaultEnabled: true } }
        : n
    )),
  }
}

export function isChatbotUnpublished(state) {
  return String(state || '').toUpperCase() !== 'LIVE'
}

function nextPrefixedId(items, prefix) {
  let max = 0
  const re = new RegExp('^' + prefix + '(\\d+)$')
  ;(items || []).forEach(item => {
    const m = String(item.id || '').match(re)
    if (m) {
      const n = parseInt(m[1], 10)
      if (n > max) max = n
    }
  })
  return prefix + (max + 1)
}

export function toFlowNodes(nodes) {
  return (nodes || []).map(n => ({
    id: n.id,
    type: n.type,
    position: {
      x: n.position && n.position.posX != null ? n.position.posX : 0,
      y: n.position && n.position.posY != null ? n.position.posY : 0,
    },
    data: {
      ...(n.data || {}),
      isStartNode: !!n.isStartNode,
      nodeType: n.type,
    },
    style: { overflow: 'visible' },
  }))
}

export function toFlowEdges(edges) {
  return (edges || []).map(e => ({
    id: e.id,
    source: e.sourceNodeId,
    target: e.targetNodeId,
    sourceHandle: edgeSourceHandle(e),
    type: EDGE_TYPE_DELETABLE,
  }))
}

export function addNode(chatbot, type, position, options) {
  const nodes = (chatbot && chatbot.nodes) || []
  const index = nodes.length
  const nodeType = type || NODE_TYPE_MESSAGE
  const prefix = nodePrefix(nodeType)
  const hasPos = position && typeof position.posX === 'number' && typeof position.posY === 'number'
  const staggerOff = options && options.stagger === false
  const stagger = hasPos && !staggerOff ? (index % 5) * 24 : 0
  const node = {
    id: nextPrefixedId(nodes, prefix),
    type: nodeType,
    position: {
      posX: hasPos ? position.posX + stagger : 80 + index * 40,
      posY: hasPos ? position.posY + stagger : 80 + index * 40,
    },
    isStartNode: nodes.length === 0,
    data: defaultNodeData(nodeType),
  }
  return {
    ...chatbot,
    nodes: nodes.concat(node),
  }
}

export function addConnectedNode(chatbot, sourceNodeId, sourceHandle, type, position) {
  if (!sourceNodeId || !type) return chatbot
  const handle = sourceHandle || HANDLE_OUT
  const sourceNode = getNodeById(chatbot, sourceNodeId)
  if (!isAllowedOutgoingHandle(sourceNode, handle)) return chatbot
  const edges = (chatbot && chatbot.edges) || []
  if (edges.some(e => e.sourceNodeId === sourceNodeId && edgeSourceHandle(e) === handle)) {
    return chatbot
  }
  const next = addNode(chatbot, type, position, { stagger: false })
  const created = next.nodes[next.nodes.length - 1]
  if (!created) return chatbot
  return addEdge(next, sourceNodeId, created.id, handle)
}

export function addEdge(chatbot, sourceNodeId, targetNodeId, sourceHandle) {
  if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return chatbot
  const handle = sourceHandle || HANDLE_OUT
  const sourceNode = getNodeById(chatbot, sourceNodeId)
  if (!isAllowedOutgoingHandle(sourceNode, handle)) return chatbot
  const targetNode = getNodeById(chatbot, targetNodeId)
  if (targetNode && targetNode.isStartNode) return chatbot
  const edges = (chatbot && chatbot.edges) || []
  if (edges.some(e => e.sourceNodeId === sourceNodeId && edgeSourceHandle(e) === handle)) {
    return chatbot
  }
  return {
    ...chatbot,
    edges: edges.concat({
      id: nextPrefixedId(edges, 'edge_'),
      sourceNodeId,
      sourceHandle: handle,
      targetNodeId,
    }),
  }
}

export function removeEdge(chatbot, edgeId) {
  const edges = (chatbot && chatbot.edges) || []
  return {
    ...chatbot,
    edges: edges.filter(e => e.id !== edgeId),
  }
}

export function removeNode(chatbot, nodeId) {
  if (!nodeId) return chatbot
  const nodes = ((chatbot && chatbot.nodes) || []).filter(n => n.id !== nodeId)
  const edges = ((chatbot && chatbot.edges) || []).filter(e => (
    e.sourceNodeId !== nodeId && e.targetNodeId !== nodeId
  ))
  let nextNodes = nodes
  if (nodes.length && !nodes.some(n => n.isStartNode)) {
    nextNodes = nodes.map((n, i) => (
      i === 0 ? { ...n, isStartNode: true } : { ...n, isStartNode: false }
    ))
  }
  return {
    ...chatbot,
    nodes: nextNodes,
    edges,
  }
}

export function duplicateNode(chatbot, nodeId) {
  const src = getNodeById(chatbot, nodeId)
  if (!src) return chatbot
  const nodes = (chatbot && chatbot.nodes) || []
  const prefix = nodePrefix(src.type)
  let data = {}
  try {
    data = JSON.parse(JSON.stringify(src.data || {}))
  } catch (e) {
    data = src.data || {}
  }
  if (src.type === NODE_TYPE_TEMPLATE) data = persistTemplateNodeData(data)
  const posX = src.position && src.position.posX != null ? src.position.posX : 0
  const posY = src.position && src.position.posY != null ? src.position.posY : 0
  return {
    ...chatbot,
    nodes: nodes.concat({
      id: nextPrefixedId(nodes, prefix),
      type: src.type,
      position: { posX: posX + 40, posY: posY + 40 },
      isStartNode: false,
      data,
    }),
  }
}

export function remapRetiredOutEdges(chatbot) {
  const nodes = (chatbot && chatbot.nodes) || []
  const edges = (chatbot && chatbot.edges) || []
  if (!edges.length) return chatbot
  let changed = false
  const nextEdges = edges.map(e => {
    const node = nodes.find(n => n.id === e.sourceNodeId)
    if (!node) return e
    const handles = getOutgoingHandles(node.data, node.type)
    const ids = handles.map(h => h.id)
    const handle = edgeSourceHandle(e)
    if (ids.indexOf(handle) !== -1) return e
    if (handle !== HANDLE_OUT) return e
    const flowHandles = handles.filter(h => h.buttonType === 'FLOW')
    if (flowHandles.length !== 1) return e
    changed = true
    return { ...e, sourceHandle: flowHandles[0].id }
  })
  if (!changed) return chatbot
  return { ...chatbot, edges: nextEdges }
}

export function updateNodePosition(chatbot, nodeId, posX, posY) {
  const nodes = (chatbot && chatbot.nodes) || []
  return {
    ...chatbot,
    nodes: nodes.map(n => (
      n.id === nodeId
        ? { ...n, position: { posX: posX, posY: posY } }
        : n
    )),
  }
}

export function updateNodeData(chatbot, nodeId, data, handleSource) {
  const nodes = (chatbot && chatbot.nodes) || []
  const nextData = data || {}
  const nextNodes = nodes.map(n => (
    n.id === nodeId
      ? { ...n, data: nextData }
      : n
  ))
  const next = remapRetiredOutEdges({ ...chatbot, nodes: nextNodes })
  const updated = getNodeById(next, nodeId)
  const handleData = handleSource
    ? Object.assign({}, nextData, handleSource)
    : nextData
  const valid = {}
  getOutgoingHandleIds(handleData, updated && updated.type).forEach(id => {
    valid[id] = true
  })
  const edges = ((next && next.edges) || []).filter(e => {
    if (e.sourceNodeId !== nodeId) return true
    const handle = edgeSourceHandle(e)
    if (valid[handle]) return true
    if (updated && updated.type === NODE_TYPE_TEMPLATE && !hasTemplateStructure(handleData)) {
      return handle !== HANDLE_OUT && isTemplateHandleWithoutStructure(handle, handleData)
    }
    return false
  })
  return {
    ...next,
    edges,
  }
}

export function toggleNodeDefault(chatbot, nodeId) {
  const node = getNodeById(chatbot, nodeId)
  if (!node) return chatbot
  const data = Object.assign({}, node.data)
  if (data.defaultEnabled) delete data.defaultEnabled
  else data.defaultEnabled = true
  return updateNodeData(chatbot, nodeId, data)
}

export function getNodeById(chatbot, nodeId) {
  const nodes = (chatbot && chatbot.nodes) || []
  return nodes.find(n => n.id === nodeId) || null
}

export function nodeHasIncoming(chatbot, nodeId) {
  if (!nodeId) return false
  return ((chatbot && chatbot.edges) || []).some(e => e.targetNodeId === nodeId)
}

export function setStartNode(chatbot, nodeId) {
  const node = getNodeById(chatbot, nodeId)
  if (!node || nodeHasIncoming(chatbot, nodeId)) return chatbot
  const nodes = (chatbot && chatbot.nodes) || []
  return {
    ...chatbot,
    nodes: nodes.map(n => ({
      ...n,
      isStartNode: n.id === nodeId,
    })),
  }
}

const LAYOUT_NODE_WIDTH = 240
const LAYOUT_NODE_HEIGHT = 120
const LAYOUT_COL_GAP = 120
const LAYOUT_ROW_GAP = 40
const LAYOUT_TREE_GAP = 80

export function autoLayoutChatbot(chatbot, measuredNodes) {
  const nodes = (chatbot && chatbot.nodes) || []
  const start = nodes.find(n => n.isStartNode)
  const nextNodes = autoLayoutNodes(nodes, chatbot && chatbot.edges, measuredNodes, start && start.id)
  if (nextNodes === nodes) return chatbot
  return {
    ...chatbot,
    nodes: nextNodes,
  }
}

export function autoLayoutNodes(nodes, edges, measuredNodes, startNodeId) {
  if (!nodes.length) return nodes
  const byId = {}
  const measured = {}
  const outgoing = {}
  const hasIncoming = {}
  nodes.forEach(n => {
    byId[n.id] = n
    outgoing[n.id] = []
  })
  ;(measuredNodes || []).forEach(m => {
    if (m && m.id) measured[m.id] = m
  })
  const size = (id) => ({
    width: (measured[id] && measured[id].width) || LAYOUT_NODE_WIDTH,
    height: (measured[id] && measured[id].height) || LAYOUT_NODE_HEIGHT,
  })
  const handleRank = (node, handle) => {
    const m = measured[node.id]
    const ids = getOutgoingHandleIds(m && m.data ? m.data : node.data, node.type)
    const i = ids.indexOf(handle)
    return i === -1 ? ids.length : i
  }
  ;(edges || []).forEach((e, order) => {
    const source = byId[e.sourceNodeId]
    if (!source || !byId[e.targetNodeId]) return
    outgoing[source.id].push({ target: e.targetNodeId, rank: handleRank(source, edgeSourceHandle(e)), order })
    hasIncoming[e.targetNodeId] = true
  })
  Object.keys(outgoing).forEach(id => {
    outgoing[id].sort((a, b) => (a.rank - b.rank) || (a.order - b.order))
  })

  const visitState = {}
  const children = {}
  const forward = {}
  const postOrder = []
  const roots = []
  const visit = (id) => {
    visitState[id] = 1
    children[id] = []
    forward[id] = []
    outgoing[id].forEach(o => {
      if (visitState[o.target] === 1) return
      forward[id].push(o.target)
      if (visitState[o.target]) return
      children[id].push(o.target)
      visit(o.target)
    })
    visitState[id] = 2
    postOrder.push(id)
  }
  nodes.filter(n => n.id === startNodeId)
    .concat(nodes.filter(n => !hasIncoming[n.id]), nodes)
    .forEach(n => {
      if (visitState[n.id]) return
      roots.push(n.id)
      visit(n.id)
    })

  const layer = {}
  postOrder.slice().reverse().forEach(id => {
    if (layer[id] == null) layer[id] = 0
    forward[id].forEach(t => {
      layer[t] = Math.max(layer[t] || 0, layer[id] + 1)
    })
  })
  const colWidth = []
  nodes.forEach(n => {
    colWidth[layer[n.id]] = Math.max(colWidth[layer[n.id]] || 0, size(n.id).width)
  })
  const colX = [0]
  for (let i = 1; i < colWidth.length; i++) colX[i] = colX[i - 1] + (colWidth[i - 1] || 0) + LAYOUT_COL_GAP

  const rowY = {}
  const shift = (id, dy) => {
    rowY[id] += dy
    children[id].forEach(k => shift(k, dy))
  }
  const place = (id, top) => {
    const height = size(id).height
    const kids = children[id]
    if (!kids.length) {
      rowY[id] = top
      return height
    }
    let cursor = top
    kids.forEach((k, i) => {
      if (i) cursor += LAYOUT_ROW_GAP
      cursor += place(k, cursor)
    })
    const first = kids[0]
    const last = kids[kids.length - 1]
    const mid = (rowY[first] + size(first).height / 2 + rowY[last] + size(last).height / 2) / 2
    let y = mid - height / 2
    if (y < top) {
      kids.forEach(k => shift(k, top - y))
      cursor += top - y
      y = top
    }
    rowY[id] = y
    return Math.max(cursor, y + height) - top
  }
  let top = 0
  roots.forEach((id, i) => {
    if (i) top += LAYOUT_TREE_GAP
    top += place(id, top)
  })

  const anchor = byId[roots[0]]
  const offsetX = ((anchor.position && anchor.position.posX) || 0) - colX[layer[anchor.id]]
  const offsetY = ((anchor.position && anchor.position.posY) || 0) - rowY[anchor.id]
  let changed = false
  const nextNodes = nodes.map(n => {
    const posX = Math.round(colX[layer[n.id]] + offsetX)
    const posY = Math.round(rowY[n.id] + offsetY)
    if (n.position && n.position.posX === posX && n.position.posY === posY) return n
    changed = true
    return { ...n, position: { posX: posX, posY: posY } }
  })
  return changed ? nextNodes : nodes
}
